import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsEvent, useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Button, Input, useInput, useViewport } from '@chilecompra/react-kit/components'
import { useLanguage } from '@chilecompra/react-kit/i18n'
import { checkInputErrorMaxLength, checkInputErrorRequired } from '@chilecompra/react-kit/utils'
import { isLocalEnv } from '../../config/settings/environment'
import { checkInputErrorPassword, checkInputErrorSamePasswords } from '../../modules/utils/checkers'
import { onResetSendRegistrationEmail, onUpdatePasswordThunk } from './RegisterWizard.actions'
import {
  DesktopNavigation,
  Hidden,
  Image,
  Instructions,
  Information,
  MobileNavigation,
  Root,
  Section,
  Title
} from './RegisterWizardPasswordGeneration.styles'

const RegisterWizardPasswordGeneration = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.registerWizard)
  const { language } = useLanguage()
  const { size } = useViewport()
  const {
    error: firstPasswordError,
    value: firstPasswordValue,
    onChange: handleFirstPasswordChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardPasswordGeneration__fieldRequiredError')),
      checkInputErrorMaxLength(16, language('RegisterWizardPasswordGeneration__fieldMaxLengthError')),
      checkInputErrorPassword(language('RegisterWizardPasswordGeneration__fieldFormatError'))
    ]
  })
  const {
    error: secondPasswordError,
    value: secondPasswordValue,
    onChange: handleSecondPasswordChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardPasswordGeneration__fieldRepeatValueError')),
      checkInputErrorSamePasswords(
        firstPasswordValue,
        language('RegisterWizardPasswordGeneration__fieldSameValueError')
      )
    ]
  })
  const handlePaste = event => event.preventDefault()
  const handleBack = () => {
    dispatch(onResetSendRegistrationEmail())
    onChangeStep('email-verify')
  }
  const handleNext = useCallback(() => {
    if (isLocalEnv) {
      onChangeStep('business-info')
      return
    }

    dispatch(
      onUpdatePasswordThunk({
        password: firstPasswordValue,
        callback: () => onChangeStep('business-info')
      })
    )
  }, [dispatch, onUpdatePasswordThunk, isLocalEnv, onChangeStep, firstPasswordValue])
  const canNotGoToNextStep = useCallback(() => {
    const hadErrors = firstPasswordError !== '' || secondPasswordError !== ''
    const hadInvalidValues = firstPasswordValue === '' || secondPasswordValue === ''
    const hadDifferentPasswordsValues = firstPasswordValue !== secondPasswordValue

    return hadErrors || hadInvalidValues || hadDifferentPasswordsValues || loading
  }, [firstPasswordError, firstPasswordValue, loading, secondPasswordError, secondPasswordValue])
  const disabledNextStep = !isLocalEnv && canNotGoToNextStep()

  useAnalyticsEvent({ action: 'Se crea usuario temporal', category: 'registrar-usuario' })
  useAnalyticsPageView('/paso/3/contraseña')

  return (
    <Root>
      <Section container>
        <Section item maxWidth="546px" md={6} xs={12}>
          <Title>{language('RegisterWizardPasswordGeneration__title')}</Title>
          <Instructions>{language('RegisterWizardPasswordGeneration__description')}</Instructions>
          {!size.isTiny && (
            <Information>
              {language('RegisterWizardPasswordGeneration__headsUp__1')}
              <br />
              {language('RegisterWizardPasswordGeneration__headsUp__2')}
            </Information>
          )}
          <Section container maxWidth={size.isDownToMedium && size.isUpToSmall ? '384px' : '100%'}>
            <Input
              required
              error={firstPasswordError}
              label={language('RegisterWizardPasswordGeneration__passwordInputLabel')}
              maxCount={16}
              type="password"
              value={firstPasswordValue}
              onChange={handleFirstPasswordChange}
            />
          </Section>
          <Section container maxWidth={size.isDownToMedium && size.isUpToSmall ? '384px' : '100%'}>
            <Input
              required
              error={secondPasswordError}
              label={language('RegisterWizardPasswordGeneration__repeatPasswordInputLabel')}
              maxCount={16}
              type="password"
              value={secondPasswordValue}
              onChange={handleSecondPasswordChange}
              onPaste={handlePaste}
            />
          </Section>
          {size.isTiny && (
            <Information>
              {language('RegisterWizardPasswordGeneration__headsUp__1')}
              <br />
              {language('RegisterWizardPasswordGeneration__headsUp__2')}
            </Information>
          )}
        </Section>
        <Hidden smDown>
          <Section item margin="-35px 0 0" md={6} padding="0 0 0 51px" xs={12}>
            <Image
              alt={language('RegisterWizardPasswordGeneration__title')}
              src="/assets/img/steps/paso4.svg?h=0af55ae1518eac79187da53c4c5702d0"
            />
          </Section>
        </Hidden>
      </Section>
      {!size.isTiny && (
        <Section container margin="52px 0 0" maxWidth="568px">
          <DesktopNavigation>
            <Button type="button" variant="text" onClick={handleBack}>
              {language('RegisterWizardPasswordGeneration__backButton')}
            </Button>
            <Button
              color="primary"
              disabled={disabledNextStep}
              loading={loading}
              margin="0 0 0 30px"
              type="button"
              onClick={handleNext}
            >
              {language('RegisterWizardPasswordGeneration__nextButton')}
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {size.isTiny && (
        <MobileNavigation>
          <Button variant="text" width="168px" onClick={handleBack}>
            {language('RegisterWizardPasswordGeneration__backButton')}
          </Button>
          <Button color="primary" disabled={disabledNextStep} loading={loading} width="168px" onClick={handleNext}>
            {language('RegisterWizardPasswordGeneration__nextButton')}
          </Button>
        </MobileNavigation>
      )}
    </Root>
  )
}

RegisterWizardPasswordGeneration.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardPasswordGeneration.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardPasswordGeneration
